import type { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@r/components/ui/form.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import type { LogicTypeStepWorkspaceWriteJsonMergePatchModel } from '@r/api/generated/client.schemas.ts';

const createFormSchema = (t: ReturnType<typeof useTranslation>['t']) =>
  z.object({
    name: z
      .string()
      .min(3, t('form.step.error.name.min'))
      .max(100, t('form.step.error.name.max'))
      .describe(t('form.schema.describe.name')),
    description: z.string().optional().describe(t('form.step.schema.describe.description')),
    stepPoint: z.coerce
      .number()
      .min(1, t('form.step.error.stepPoint.min'))
      .describe(t('form.step.schema.describe.step.point'))
  });

type FormSchema = ReturnType<typeof createFormSchema>;

interface StepFormProps {
  onSubmitStep: (step: LogicTypeStepWorkspaceWriteJsonMergePatchModel) => void;
}

export const StepForm: FC<StepFormProps> = ({ onSubmitStep }) => {
  const { t } = useTranslation();

  const schema = createFormSchema(t);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      stepPoint: 0
    }
  });

  function onSubmit(data: z.infer<FormSchema>) {
    onSubmitStep(data as LogicTypeStepWorkspaceWriteJsonMergePatchModel);
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit(onSubmit)(e);
  };

  return (
    <Form {...form} schema={schema}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.step.name.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('form.step.name.label')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.step.description.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('form.step.description.label')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="stepPoint"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.step.point.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('form.step.point.label')}
                    type="number"
                    min={1}
                    step={1}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button rounded="full" className="w-full" type="submit">
            {t('form.step.submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
